import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode } from 'react';
import { Flag } from '@phosphor-icons/react';
import { LessonPreview, type ChapterBadge, type LessonPreviewAction, type LessonPreviewHandle } from '../LessonPreview';
import type { LiveChapter } from '../../types/liveCall';
import type { NotableSegment, VoiceoverNotableSegmentClip } from '../../types/videoEdit';
import { MAX_CHAPTERS, newSegment, segmentMarks, sideBySideClip, withChapter } from '../../utils/liveChapters';
import { SegmentCaptureModal } from '../SegmentCaptureModal';
import { BTN_PRIMARY } from '../buttonStyles';
import { useToast } from '../../hooks/useToast';
import { useFullscreen } from '../../hooks/useFullscreen';

/**
 * A lesson's match in the async lesson player (decision 103), with the
 * coach's saved moments (decision 128), kept on the booking as liveChapters.
 * The same on the coach's lesson page before the call and in the call, so
 * moments saved while preparing are there when it starts.
 *
 * The coach's Flag is the async editor's: it pauses, takes the 15 seconds
 * before the playhead, and opens the editor's own segment modal (its
 * handles, a title, a note); Save Segment keeps it. The moments show on the
 * player's bar, the whole video still there between them, and in its panel,
 * "Ryan's saved moments"; a tap on one jumps there.
 */
/** The editor's capture window (LessonVideoEditor CAPTURE_SECONDS). */
const CAPTURE_SECONDS = 15;
export interface ChapteredMatchProps {
  videoUrl: string;
  /** The video's name, for a panel with no chapters. */
  label: string;
  /** Distinguishes two panels' clips. */
  panel?: number;
  chapters?: LiveChapter[] | null;
  /** The coach, who flags. */
  canFlag?: boolean;
  /** The list with the new chapter, to save. */
  onChaptersChange?: (next: LiveChapter[]) => void;
  /** Fill the parent (the call's stage), not a 16:9 box. */
  fill?: boolean;
  chaptersOpenAtStart?: boolean;
  /** The chapters as a column beside the picture (a wide stage of its own); else an overlay. */
  chaptersColumn?: boolean;
  /** Space above that column, for a control over its corner. */
  chaptersTopInset?: number;
  className?: string;
  overlay?: ReactNode;
  onUserAction?: (action: LessonPreviewAction) => void;
  /** The video paused for a flag, by this side (the call says so to the other side). */
  onFlagPause?: () => void;
  /** The coach's first name: the panel is "<name>'s saved moments". */
  coachFirst?: string;
  /** Its full-screen button takes the browser full screen, as the delivered lesson's does (the lesson page). */
  fullscreen?: boolean;
  /** The saved moments' panel. Off on the lesson pages (decision 137): marks on the bar only, titles on hover. */
  chaptersPanel?: boolean;
  /**
   * In the call, the moments' side-by-sides (145, 149): each moment with one shows it on its marker and
   * its row, open while `openUrl` is its clip; the coach's `onToggle` opens or closes it from either.
   */
  sideBySide?: { openUrl: string | null; matchUrl: string; onToggle?: (c: LiveChapter) => void };
  /** Load the video with CORS, for the call's recording (LessonPreview's crossOrigin). */
  crossOrigin?: 'anonymous';
  /** In the call: its full-screen button makes the call's stage the match's (162), never the system player. */
  stageFullscreen?: { on: boolean; toggle: () => void };
  /** Prep's Flag (161): "below" is the large primary under the player, in the brand blue; else the glass overlay. */
  flagPlacement?: 'overlay' | 'below';
  /** A capture began (Prep's one-time hint goes). */
  onFlag?: () => void;
}

const mmss = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

export const ChapteredMatch = forwardRef<LessonPreviewHandle, ChapteredMatchProps>(function ChapteredMatch({
  videoUrl, label, panel = 0, chapters, canFlag = false, onChaptersChange, fill = false, chaptersOpenAtStart = false, chaptersColumn, chaptersTopInset,
  className = '', overlay, onUserAction, onFlagPause, fullscreen = false, coachFirst, chaptersPanel = true, sideBySide, crossOrigin, stageFullscreen,
  flagPlacement = 'overlay', onFlag,
}, ref) {
  const inner = useRef<LessonPreviewHandle>(null);
  // Each call goes to the player's handle as it is now: it is rebuilt when its
  // clips change (the length arriving), and a copy taken at mount drove the
  // old one, so the other side's moves did not land.
  useImperativeHandle(ref, () => ({
    seek: (t) => inner.current?.seek(t),
    scrubStart: () => inner.current?.scrubStart(),
    scrubTo: (t) => inner.current?.scrubTo(t),
    scrubEnd: (t) => inner.current?.scrubEnd(t),
    play: () => inner.current?.play(),
    pause: () => inner.current?.pause(),
    setSpeed: (r) => inner.current?.setSpeed(r),
    getTime: () => inner.current?.getTime() ?? 0,
    isPlaying: () => inner.current?.isPlaying() ?? false,
    getVideoElement: () => inner.current?.getVideoElement() ?? null,
  }), []);

  // The video's length, from its metadata: the player reads a clip's stored
  // length and never probes an HTTP video, so without it the bar is 0:00 and
  // a drag does nothing.
  const [measured, setMeasured] = useState<{ url: string; length: number } | null>(null);
  const length = measured?.url === videoUrl ? measured.length : null;
  useEffect(() => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) setMeasured({ url: videoUrl, length: v.duration });
      v.removeAttribute('src');
      v.load();
    };
    v.src = videoUrl;
    return () => { v.onloadedmetadata = null; v.removeAttribute('src'); v.load(); };
  }, [videoUrl]);

  // One clip, its URL known, its length, its segment: made once per video.
  // The player copies its segments prop into state whenever it changes, so a
  // new array each render re-rendered it without end.
  const lesson = useMemo(() => {
    const id = `match-${panel}`;
    const clips: VoiceoverNotableSegmentClip[] = [{ id, insertionPoint: 0, segmentIds: [id], ...(length ? { videoDuration: length } : {}) }];
    const segments: NotableSegment[] = [{ id, label, startTime: 0, endTime: 0 }];
    return { clips, urls: new Map([[id, videoUrl]]), segments };
  }, [videoUrl, label, panel, length]);
  // A moment's side-by-side, on its marker and its row: the coach's to open and close, a mark for the player.
  const openUrl = sideBySide?.openUrl ?? null;
  const matchUrl = sideBySide?.matchUrl ?? '';
  const onToggle = sideBySide?.onToggle;
  const hasSideBySides = !!sideBySide;
  const badgeOf = useCallback((c: LiveChapter): ChapterBadge | undefined => {
    const clip = hasSideBySides ? sideBySideClip(c, matchUrl) : null;
    if (!clip) return undefined;
    const name = c.label.trim() || 'this moment';
    const on = openUrl === clip.url;
    if (!onToggle) return { label: `Side by side for ${name}`, on };
    return { label: on ? `Close the side by side for ${name}` : `Open the side by side for ${name}`, on, onToggle: () => onToggle(c) };
  }, [hasSideBySides, matchUrl, openUrl, onToggle]);
  const marks = useMemo(() => {
    const byId = new Map((chapters ?? []).map((c) => [c.id, c]));
    return segmentMarks(chapters, length, label).map((m) => {
      const c = m.id ? byId.get(m.id) : undefined;
      const badge = c ? badgeOf(c) : undefined;
      return badge ? { ...m, badge } : m;
    });
  }, [chapters, length, label, badgeOf]);
  // The panel lists every saved moment, overlapping ones too (the bar cannot draw an overlap), each with its note (117).
  const moments = useMemo(() => [...(chapters ?? [])]
    .sort((x, y) => x.startTime - y.startTime)
    .map((c) => {
      const badge = badgeOf(c);
      return { label: c.label.trim() || 'Saved moment', start: c.startTime, end: Math.max(c.endTime, c.startTime), ...(c.note ? { note: c.note } : {}), ...(badge ? { badge } : {}) };
    }),
  [chapters, badgeOf]);
  const savedCount = moments.length;

  // The delivered lesson's full screen: the Fullscreen API on this wrapper,
  // the player's own chrome inside it, Escape or the button to leave.
  const { containerRef, isFullscreen, toggle } = useFullscreen();
  const expanded = fullscreen && isFullscreen;

  // ── Flag: the editor's capture, the 15 seconds before the playhead ──────
  const { showInfo, showSuccess } = useToast();
  const [capture, setCapture] = useState<{ start: number; end: number } | null>(null);
  /** Where the coach left the modal, kept from one capture to the next while this is open. */
  const [capturePos, setCapturePos] = useState<{ x: number; y: number } | null>(null);
  const full = (chapters?.length ?? 0) >= MAX_CHAPTERS;
  const startFlag = () => {
    const r = inner.current;
    if (!r || capture) return;
    const t = r.getTime();
    if (t < 1) { showInfo('Nothing to capture yet'); return; }
    if (r.isPlaying()) { r.pause(); onFlagPause?.(); }
    setCapture({ start: Math.max(0, t - CAPTURE_SECONDS), end: t });
    onFlag?.();
  };
  const saveCapture = (start: number, end: number, name: string, note?: string) => {
    const seg = newSegment(start, end, name, note);
    const next = withChapter(chapters, seg);
    if (next) onChaptersChange?.(next);
    setCapture(null);
    // Playback is not resumed: the coach decides when to carry on (as the editor).
    showSuccess(`"${seg.label}" saved (${mmss(seg.startTime)} – ${mmss(seg.endTime)})`);
  };

  return (
    <div ref={fullscreen ? containerRef : undefined}
      className={expanded ? 'fixed inset-0 z-50 flex flex-col bg-black' : `relative ${fill ? 'flex h-full w-full' : ''} ${className}`}
      data-chaptered-match={panel}>
      {flagPlacement === 'below' ? (
        // Prep: the picture in its own black frame, the large Flag under it on the card.
        <div className={`overflow-hidden bg-black ${expanded ? 'flex min-h-0 flex-1 flex-col' : 'rounded-[8px]'}`} data-prep-frame>
        <LessonPreview
          ref={inner}
          variant="native"
          fill={fill}
          crossOrigin={crossOrigin}
          className={expanded ? 'min-h-0 flex-1' : fill ? 'h-full w-full' : 'w-full'}
          clips={lesson.clips}
          preloadedClipUrls={lesson.urls}
          notableSegments={lesson.segments}
          chapterMarks={marks}
          chaptersTitle={coachFirst ? `${coachFirst}'s saved moments` : 'Saved moments'}
          chapterList={length && chaptersPanel ? moments : undefined}
          chaptersPanel={chaptersPanel}
          // Open at first only with chapters to show: an empty list covered the picture.
          chaptersOpenAtStart={chaptersPanel && chaptersOpenAtStart && savedCount > 0}
          chaptersColumn={chaptersColumn}
          chaptersTopInset={chaptersTopInset}
          onUserAction={onUserAction}
          overlay={overlay}
          {...(fullscreen ? { isFullscreen, onFullscreenToggle: toggle }
            : stageFullscreen ? { isFullscreen: stageFullscreen.on, onFullscreenToggle: stageFullscreen.toggle, systemFullscreen: false } : {})}
        />
        </div>
      ) : (
        <LessonPreview
          ref={inner}
          variant="native"
          fill={fill}
          crossOrigin={crossOrigin}
          className={expanded ? 'min-h-0 flex-1' : fill ? 'h-full w-full' : 'w-full'}
          clips={lesson.clips}
          preloadedClipUrls={lesson.urls}
          notableSegments={lesson.segments}
          chapterMarks={marks}
          chaptersTitle={coachFirst ? `${coachFirst}'s saved moments` : 'Saved moments'}
          chapterList={length && chaptersPanel ? moments : undefined}
          chaptersPanel={chaptersPanel}
          // Open at first only with chapters to show: an empty list covered the picture.
          chaptersOpenAtStart={chaptersPanel && chaptersOpenAtStart && savedCount > 0}
          chaptersColumn={chaptersColumn}
          chaptersTopInset={chaptersTopInset}
          onUserAction={onUserAction}
          overlay={overlay}
          {...(fullscreen ? { isFullscreen, onFullscreenToggle: toggle }
            : stageFullscreen ? { isFullscreen: stageFullscreen.on, onFullscreenToggle: stageFullscreen.toggle, systemFullscreen: false } : {})}
        />
      )}
      {canFlag && flagPlacement === 'below' && (
        <button type="button" onClick={startFlag} disabled={full || !!capture}
          className={`${BTN_PRIMARY} mt-4 w-full`} aria-label="Flag"
          title={full ? 'This lesson has 50 saved moments' : 'Save the last 15 seconds'} data-flag data-flag-large>
          <Flag size={20} weight="fill" aria-hidden="true" />Flag
        </button>
      )}
      {canFlag && flagPlacement === 'overlay' && !capture && (
        <button type="button" onClick={startFlag} disabled={full}
          className="call-glass ios-press absolute left-3 top-3 z-[6] flex h-11 items-center gap-1.5 rounded-full px-4 text-[15px] leading-[20px] font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-50"
          aria-label="Flag" title={full ? 'This lesson has 50 saved moments' : 'Save the last 15 seconds'} data-flag>
          <Flag size={18} weight="fill" aria-hidden="true" />Flag
        </button>
      )}
      {canFlag && capture && length && (
        <SegmentCaptureModal
          captureStart={capture.start}
          captureEnd={capture.end}
          duration={length}
          defaultName={`Part ${(chapters?.length ?? 0) + 1}`}
          onSeek={(t) => inner.current?.seek(t)}
          onSave={saveCapture}
          onCancel={() => setCapture(null)}
          position={capturePos}
          onPositionChange={setCapturePos}
        />
      )}
    </div>
  );
});

